import { Dialog, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useOpen } from '@/app/hooks/useOpen';
import { forwardRef, useImperativeHandle, useCallback, useState } from 'react';
import { useGetPaymentsDocumentItemQuery } from '@/app/api/paymentsApi';

import ModalTemplate from '@/shared/modal/modal-template';
import { UIFormLabel } from '@/shared/ui/ui-form-label';
import FullscreenLoader from '@/shared/FullscreenLoader/FullscreenLoader';
import ComponentWithLoader from '@/shared/ComponentWithLoader/ComponentWithLoader';
import { MoneyFlowModalRef } from '../factor-money-flow/types';
import { triada } from '@/app/helpers';

export const MoneyFlowModal = forwardRef<MoneyFlowModalRef, {}>(
  (_props, ref) => {
    const { t } = useTranslation();
    const { open, setOpen } = useOpen();
    const [documentId, setDocumentId] = useState<string | null>(null);

    const { data, isLoading } = useGetPaymentsDocumentItemQuery(
      { id: documentId || '' },
      {
        skip: !documentId,
      }
    );

    const onOpen = useCallback(
      (unique: number) => {
        setDocumentId(String(unique));
        setOpen(true);
      },
      [setOpen]
    );

    const onClose = useCallback(() => {
      setOpen(false);
      setDocumentId(null);
    }, [setOpen]);

    useImperativeHandle(
      ref,
      () => ({
        open: onOpen,
        close: onClose,
      }),
      [onOpen, onClose]
    );

    return (
      <>
        <Dialog
          open={open}
          onClose={onClose}
          PaperProps={{
            sx: {
              maxWidth: 640,
              width: '100%',
            },
          }}
        >
          <ModalTemplate
            title={t('payment_document.title')}
            onClose={onClose}
            cancelBtnText={t('close')}
          >
            <ComponentWithLoader isLoading={isLoading}>
              {data?.data && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <UIFormLabel
                      sx={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: 'primary.main',
                      }}
                    >
                      {t('payment_type')}:
                    </UIFormLabel>

                    <Typography
                      sx={{
                        fontSize: 14,
                        color: 'text.primary',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {data.data.paymentTypeId === 'payout'
                        ? t('payment_type_options.payout')
                        : t('payment_type_options.redeem')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <UIFormLabel
                      sx={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: 'primary.main',
                      }}
                    >
                      {t('recipient')}:
                    </UIFormLabel>

                    <Typography
                      sx={{
                        fontSize: 14,
                        color: 'text.primary',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {data.data.recipient.name}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <UIFormLabel
                      sx={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: 'primary.main',
                      }}
                    >
                      {t('recipient_tin')}:
                    </UIFormLabel>

                    <Typography
                      sx={{
                        fontSize: 14,
                        color: 'text.primary',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {data.data.recipient.tin}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <UIFormLabel
                      sx={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: 'primary.main',
                      }}
                    >
                      {t('payer')}:
                    </UIFormLabel>

                    <Typography
                      sx={{
                        fontSize: 14,
                        color: 'text.primary',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {data.data.payer.name}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <UIFormLabel
                      sx={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: 'primary.main',
                      }}
                    >
                      {t('payer_tin')}:
                    </UIFormLabel>

                    <Typography
                      sx={{
                        fontSize: 14,
                        color: 'text.primary',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {data.data.payer.tin}
                    </Typography>
                  </Grid>

                  {data.data.recipient.account && (
                    <Grid item xs={12}>
                      <UIFormLabel
                        sx={{
                          fontSize: 14,
                          fontWeight: 500,
                          color: 'primary.main',
                        }}
                      >
                        {t('recipient_account')}:
                      </UIFormLabel>

                      <Typography
                        sx={{
                          fontSize: 14,
                          color: 'text.primary',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {data.data.recipient.account
                          ?.replace(/(.{4})/g, '$1 ')
                          .trim()}
                      </Typography>
                    </Grid>
                  )}

                  <Grid item xs={12} md={6}>
                    <UIFormLabel
                      sx={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: 'primary.main',
                      }}
                    >
                      {t('amount')}:
                    </UIFormLabel>

                    <Typography
                      sx={{
                        fontSize: 14,
                        color: 'text.primary',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {triada(data.data.amount.toFixed(2))}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <UIFormLabel
                      sx={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: 'primary.main',
                      }}
                    >
                      {t('date')}:
                    </UIFormLabel>

                    <Typography
                      sx={{
                        fontSize: 14,
                        color: 'text.primary',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {data.data.date}
                    </Typography>
                  </Grid>
                </Grid>
              )}
            </ComponentWithLoader>
          </ModalTemplate>
        </Dialog>
        {isLoading && <FullscreenLoader />}
      </>
    );
  }
);
